import { useCallback } from 'react';
import { api, apiEndpoints } from '@selfserve/react-utils';

const NX_GOOGLE_CLIENT_ID = process.env['NX_GOOGLE_CLIENT_ID'];

export interface GoogleOAuthResult {
  token: string;
  email: string;
}

// GsiButtonConfiguration.text from https://developers.google.com/identity/gsi/web/reference/js-reference
export enum GoogleButtonText {
  SIGN_IN_WITH = 'signin_with',
  SIGN_UP_WITH = 'signup_with',
  CONTINUE_WITH = 'continue_with',
  SIGN_IN = 'signin',
}

export interface UseGoogleOAuthProps {
  onSuccess: (result: GoogleOAuthResult) => void;
  onError: (error: Error) => void;
  onClick?: () => void;
  text?: GoogleButtonText;
}

interface GoogleCredentialResponse {
  credential: string;
}

// decodes the email claim out of the JWT ID token issued by Google Identity Services
const getEmailFromIdToken = (idToken: string): string => {
  const [, payload] = idToken.split('.');
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json).email;
};

// Google only allows one active button/callback per page - so we track it here at module level
// and re-render the button whenever the nonce changes
let initPromise: Promise<void> | null = null;
let activeOnSuccess: ((result: GoogleOAuthResult) => void) | null = null;
let activeOnError: ((error: Error) => void) | null = null;
let activeOnClick: (() => void) | null = null;
let activeNode: HTMLDivElement | null = null;
let activeText: GoogleButtonText | undefined;
let lastCredential: string | null = null;

const renderButton = () => {
  const google = (window as any).google;
  if (!activeNode || !google?.accounts?.id) return;

  activeNode.innerHTML = '';
  google.accounts.id.renderButton(activeNode, {
    type: 'standard', // Google Identity Services default is "standard"
    theme: 'outline', // Google Identity Services default is "outline"
    size: 'large', // Google Identity Services default is "large"
    text: activeText || GoogleButtonText.SIGN_IN_WITH, // Google Identity Services default is "signin_with"
    width: '400', // Google Identity Services default native max width is "400" - GoogleButton scales it to fit wider containers
    click_listener: () => activeOnClick?.(),
  });
};

// gets a new nonce from the server and sets up Google identification to use it for the next attempt
const initializeWithFreshNonce = (): Promise<void> => {
  return api
    .get<{ nonce: string }>(apiEndpoints.googleNonce)
    .then(({ data }) => {
      const nonce = data?.nonce;
      if (!nonce) {
        throw new Error('Failed to obtain Google SSO nonce');
      }

      const google = (window as any).google;
      google.accounts.id.initialize({
        client_id: NX_GOOGLE_CLIENT_ID,
        use_fedcm_for_button: true, // shows the centered FedCM account chooser
        nonce,
        callback: ({ credential }: GoogleCredentialResponse) => {
          // the nonce is single-use and consumed server-side as soon as a credential redeems it
          // so a fresh nonce must be fetched and re-rendered before the next click -
          // otherwise a failed or retried sign-in would keep presenting an already-consumed nonce
          if (credential === lastCredential) return;
          lastCredential = credential;

          try {
            activeOnSuccess?.({ token: credential, email: getEmailFromIdToken(credential) });
          } finally {
            startInitialization()
              .then(renderButton)
              .catch((error) => activeOnError?.(error as Error));
          }
        },
      });
    });
};

// a failed attempt is forgotten so the next mount retries instead of reusing the rejection
const startInitialization = (): Promise<void> => {
  const attempt: Promise<void> = initializeWithFreshNonce().catch((error) => {
    if (initPromise === attempt) initPromise = null;
    throw error;
  });
  initPromise = attempt;
  return attempt;
};

// ensures initialize() only ever has one call in flight/registered at a time
const ensureInitialized = (): Promise<void> => initPromise ?? startInitialization();

export const useGoogleOAuth = ({ onSuccess, onError, onClick, text }: UseGoogleOAuthProps) => {
  // set here (and not in useEffect) so the ref callback always sees the latest values
  activeOnSuccess = onSuccess;
  activeOnError = onError;
  activeOnClick = onClick || null;
  activeText = text;

  const googleButtonRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      activeNode = null;
      return;
    }

    const google = (window as any).google;
    if (!google?.accounts?.id) {
      onError(new Error('Google Identity Services not loaded'));
      return;
    }

    activeNode = node;

    ensureInitialized()
      .then(renderButton)
      .catch((error) => onError(error as Error));
  }, []);

  return { googleButtonRef };
};
